import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

let socket: Socket | null = null;

/** One websocket per tab, same origin (cookies carry the session). */
export function getSocket(): Socket {
  if (!socket) {
    socket = io({ path: '/socket.io', withCredentials: true, transports: ['websocket', 'polling'] });
  }
  return socket;
}

export function useConnected() {
  const [connected, setConnected] = useState(() => getSocket().connected);
  useEffect(() => {
    const s = getSocket();
    const on = () => setConnected(true);
    const off = () => setConnected(false);
    s.on('connect', on);
    s.on('disconnect', off);
    return () => {
      s.off('connect', on);
      s.off('disconnect', off);
    };
  }, []);
  return connected;
}

/**
 * Subscribes to a room (re-subscribing after reconnects) and listens to events.
 * `subscribe` is the server event that joins the room, e.g. 'subscribe:restaurant'.
 */
export function useRealtime(
  subscribe: { event: string; payload?: unknown } | null,
   
  handlers: Record<string, (payload: any) => void>,
  deps: unknown[] = [],
) {
  const ref = useRef(handlers);
  ref.current = handlers;
  useEffect(() => {
    const s = getSocket();
    const join = () => {
      if (subscribe) s.emit(subscribe.event, subscribe.payload ?? {}, () => {});
    };
    const names = Object.keys(ref.current);
    const wrapped = names.map((n) => [n, (p: unknown) => ref.current[n]?.(p)] as const);
    for (const [n, fn] of wrapped) s.on(n, fn);
    s.on('connect', join);
    if (s.connected) join();
    return () => {
      for (const [n, fn] of wrapped) s.off(n, fn);
      s.off('connect', join);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe?.event, JSON.stringify(subscribe?.payload ?? null), ...deps]);
}
